<template>
  <div v-if="loading" class="container">
    <LoadingAnimation :information-message="informationMessage" />
  </div>

  <div v-else>
    <br />
    <br />

    <div class="container">
      <div class="grid-container">
        <div class="grid-x grid-margin-x">
          <div class="cell small-12 large-12">
            <div class="banner-carousel">
              <div class="banner-content grid-x grid-margin-x align-middle">
                <div class="cell show-for-medium medium-4 banner__image">
                  <img src="/images/support.png" alt="Person using laptop" />
                </div>

                <div class="cell small-12 medium-8 banner__copy">
                  <h1 class="white">Hi {{ identity.account?.name }}!</h1>

                  <p class="white">
                    Welcome to your <strong>Client Onboarding Dashboard</strong>.
                    <br />
                    Here, you can create new onboarding projects, and manage your existing projects.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div class="cell small-12 medium-5 large-6 margin-top-1 medium-margin-top-2">
            <h2>Onboarding Projects</h2>
          </div>

          <div class="cell small-12 medium-7 large-6 margin-top-1 medium-margin-top-2">
            <div class="client-list-controls">
              <DisplayChangeControl
                title=""
                :show-buttons="false"
                :controls="filterControls"
                @update:filters="handleFilters"
              />
            </div>
          </div>

          <div class="cell small-12 large-12 medium-margin-top-2">
            <ClientProjectsTable
              :clients="visibleClients"
              :columns="columns"
              :sort="sort"
              @select-client="selectClient"
              @sort="toggleSort"
            />

            <p v-if="!visibleClients.length" class="client-list-empty">
              No clients match the current search and filters.
            </p>

            <br />
            <router-link :to="addClientRoute" class="btn btn-primary">
              {{ addClientLabel }}
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { inject, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useIdentityStore } from '@/stores/identity';
import { GetClientSummaries } from '@/services/implementationmanagement.api';
import LoadingAnimation from '@/components/LoadingAnimation.vue';
import DisplayChangeControl from '@/components/controls/DisplayChangeControl.vue';
import ClientProjectsTable from '@/components/clients/ClientProjectsTable.vue';
import { useClientList } from '@/composables/useClientList';

const props = defineProps({
  columns: {
    type: Array,
    required: true,
  },
  addClientLabel: {
    type: String,
    required: true,
  },
  addClientRoute: {
    type: [String, Object],
    required: true,
  },
  showClientTypeFilter: {
    type: Boolean,
    default: false,
  },
});

const msalInstance = inject('msalInstance');
const router = useRouter();
const identity = useIdentityStore();
const clients = ref([]);
const loading = ref(false);
const informationMessage = ref('Loading your clients...');

import { computed } from 'vue';

const filterControls = computed(() => {
  const controls = [
    {
      key: 'ClientName',
      type: 'text',
      default: '',
      props: {
        label: 'Search clients',
        placeholder: 'Search by client name',
      },
    },
    {
      key: 'ProjectStatus',
      type: 'select',
      default: '',
      props: {
        label: 'Filter by Status',
        options: [
          {
            value: '',
            label: 'All',
          },
          {
            value: 'Active',
            label: 'Active',
          },
          {
            value: 'Complete',
            label: 'Complete',
          },
        ],
      },
    },
  ];

  if (props.showClientTypeFilter) {
    controls.push({
      key: 'ClientType',
      type: 'select',
      default: '',
      props: {
        label: 'Filter by Client Type',
        options: [
          {
            value: '',
            label: 'All',
          },
          {
            value: 'Direct',
            label: 'Direct Client',
          },
          {
            value: 'Intermediary',
            label: 'Intermediary Client',
          },
        ],
      },
    });
  }

  return controls;
});

const {
  filters,
  searchTerm,
  sort,
  visibleClients,
  handleFilters,
  toggleSort,
} = useClientList(clients);

onMounted(loadClients);

async function loadClients() {
  loading.value = true;
  informationMessage.value = 'Loading your clients...';

  try {
    await identity.refreshClaims(msalInstance);

    const identityClients = Array.isArray(identity.clients)
      ? identity.clients
      : identity.clients
        ? [identity.clients]
        : [];

    if (identityClients.length === 0) {
      if(identity.type === 'Administrator') {
        await router.replace('/admin/clients/add');
      } else {
        await router.replace('/clients/add');
      }
      
      return;
    }

    const response = await GetClientSummaries(identityClients);

    if (response.status !== 200 || !Array.isArray(response.data)) {
      throw new Error('Unable to load client summaries.');
    }

    clients.value = response.data;
  } catch (error) {
    console.error('Failed to load clients:', error);
    informationMessage.value = 'Error loading client summaries.';
  } finally {
    loading.value = false;
  }
}

async function selectClient(clientId) {
  if (!clientId) return;

  loading.value = true;
  informationMessage.value = 'Opening client...';

  try {
    await identity.selectClient(clientId);
    await router.push({ name: 'Home' });
  } catch (error) {
    console.error('Failed to select client:', error);
    informationMessage.value = 'Error opening client.';
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped>
.client-list-controls {
  width: 100%;
}

.client-list-empty {
  margin-top: 1rem;
}


</style>
